import { Email, Search, Sms } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Box, Card, Grid, Menu, MenuItem } from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import { BiFilterAlt } from 'react-icons/bi';
import { LuCopyCheck } from 'react-icons/lu';
import {
  ICandidate,
  ICandidateSubmissionSearchFilterVM,
  IDownloadPermission,
  NewSubmissionDownloadPayLoad,
  amplitude,
  getDataDownloadSettings,
  hasDownloadPermissions,
} from 'wisestep-api';
import {
  SearchBar,
  WsButton,
  WsColors,
  WsDrawer,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
} from 'wisestep-base';

import { accessDeniedNotoficationTxt } from '../../../../components/AccessControl/AccessControl';
import { ActivateFeature } from '../../../../components/ActivateFeature';
import BulkEmail from '../../../../components/BulkEmail';
import BulkSmsDrawer from '../../../../components/BulkSms/BulkSmsDrawer';
import BulkUpdateStatusDrawer from '../../../../components/BulkUpdateStatusDrawer';
import { useWorkspaceContext } from '../../../../context';
import { handleSubmissionCandidateDownload } from '../../../../features/dataDownload/helper/PayloadHelper';
import { handleSelectedCandidatesLimit } from '../../../../features/jobsCrm/components/helper/SendSmsEmailFuntionality';
import DownloadField from '../../../../features/submissions/submissionListing/components/Download';
import { useCandidateAts, CandidateAtsAccess } from '../../../../hooks/useCandidateAts';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { failed, success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import SubmitCandidate from '../../../conversationWidget/components/SubmitCandidate';
import conversationWidgetStore from '../../../conversationWidget/store/conversationWidgetStore';

export interface ISubmissionsFilters {
  onAdvancedFiltersClick: () => void;
  searchQuery: ICandidateSubmissionSearchFilterVM;
  selectedCandidates: ICandidate[];
  updateUI: (candidate: ICandidate[]) => void;
  setOpenAdvancedFilters: Dispatch<SetStateAction<boolean>>;
  getUpdatedFiltersFromFilters: (data) => void;
  clear: () => void;
}
const Filters = ({
  onAdvancedFiltersClick,
  searchQuery,
  selectedCandidates,
  updateUI,
  getUpdatedFiltersFromFilters,
  clear,
}: ISubmissionsFilters) => {
  const methods = useForm<ICandidateSubmissionSearchFilterVM>({ defaultValues: searchQuery });
  const { watch } = methods;
  const workspace = useWorkspaceContext();
  const [openSubmitCandidate, setOpenSubmitCandidate] = useState(false);
  const [openStageStatusDrawer, setOpenStageStatusDrawer] = useState(false);
  const [openEmail, setOpenEmail] = useState<boolean>(false);
  const [openBulkSms, setOpenBulkSms] = useState<boolean>(false);

  const isSubmitCandidate = useCandidateAts(CandidateAtsAccess.SUBMIT_CANDIDATE);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const selectedJob = conversationWidgetStore((state) => state.selectedJob);

  const onSearch = () => getClientDataList();

  const getClientDataList = useCallback(async () => {
    const searchText = watch('query') ?? '';
    const updatedQuery = searchQuery;
    const query: string = searchText ?? '';
    updatedQuery.query = query;
    getUpdatedFiltersFromFilters({ ...updatedQuery });
  }, [searchQuery, getUpdatedFiltersFromFilters, watch]);

  const submissionSearchAmplitudeEvent = (name: string, clickedOnName: string) =>
    amplitude(name, {
      clickedOn: clickedOnName,
      workspaceName: workspace.name,
      pageName: 'submissionListing',
      planType: workspace.planType,
      interfaceType: 'web',
      pageUrl: window.location.href,
    });
  const handleAdvanceFiltersOnClick = () => {
    submissionSearchAmplitudeEvent('Submission_Advance_filters', 'Advance Filters');
    onAdvancedFiltersClick();
  };

  const sendEmail = () =>
    selectedCandidates.length > 50
      ? setNotification(failed('Cannot send emails to more than 50 candidates in a campaign'))
      : setOpenEmail(true);

  const sendSms = () =>
    selectedCandidates.length > 50
      ? setNotification(
          failed('You cannot send SMS messages to more than 50 candidates in a campaign')
        )
      : setOpenBulkSms(true);

  const submissionAmplitudeEvent = (name: string, clickedOnName: string) =>
    amplitude(name, {
      clickedOn: clickedOnName,
      workspaceName: workspace.name,
      pageName: 'Submissions',
      planType: workspace.planType,
      interfaceType: 'web',
      pageUrl: window.location.href,
    });

  const [openDownload, setOpenDownload] = useState<boolean>(false);
  const [type, setType] = useState<string>('');
  const [permissionState, setPermissionState] = useState<boolean>(false);

  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(downloadAnchor);
  const isViewDownload = useCandidateAts(CandidateAtsAccess.SUBMISSIONS_DOWNLOAD);

  const handleOpenDownloadOptions = (event: React.MouseEvent<HTMLElement>) =>
    setDownloadAnchor(event.currentTarget);
  useEffect(() => {
    const permissionData = async () => {
      const permissionResponse = await hasDownloadPermissions();
      const permissionResponseData: IDownloadPermission = permissionResponse.data;
      setPermissionState(permissionResponseData.CS_DOWNLOAD_CANDIDATE_LIST);
    };

    permissionData();
  }, []);
  const onClickDownload = async (type: string) => {
    setOpenDownload(true);
    setDownloadAnchor(null);
    setType(type);
  };
  const downloadCandidates = async (v: string[]) => {
    try {
      const downloadedInfo = await getDataDownloadSettings();
      const downloadStatus = await handleSubmissionCandidateDownload(type, {
        ...searchQuery,
        size: downloadedInfo.data.recordsDownloadLimit,
        page: 0,
        columns: v,
      } as NewSubmissionDownloadPayLoad);
      if (downloadStatus == 'success') setNotification(success('Downloaded candidates'));
      else setNotification(warning('Something went wrong, Please try again.'));
    } catch (e) {
      setNotification(warning('Something went wrong, Please try again.'));
    } finally {
      setOpenDownload(false);
    }
  };

  return (
    <Box
      bgcolor={WsColors.White}
      sx={{
        border: '1px  solid #E0E0E0',
        padding: '16px',
        borderTopLeftRadius: 2,
        borderTopRightRadius: 2,
        borderBottom: 'none',
      }}
    >
      <Grid container bgcolor={WsColors.White}>
        <Grid container justifyContent="flex-start" spacing={1} xs={7}>
          <FormProvider {...methods}>
            <Grid item xs={8}>
              <Card sx={{ p: 0.5, height: 32 }}>
                <SearchBar
                  name="query"
                  placeholder={'Search for Candidate, Skill, Job Title, Location'}
                />
              </Card>
            </Grid>
            <Grid item xs={4} gap={1} display="flex">
              <WsButton
                className="advance-filter"
                variant="outlined"
                size="medium"
                type="reset"
                onClick={() => {
                  methods.setValue('query', '');
                  clear();
                }}
              >
                Clear
              </WsButton>
              <WsButton
                className="advance-filter"
                variant="contained"
                size="medium"
                type="submit"
                onClick={onSearch}
                startIcon={<Search />}
              >
                Search
              </WsButton>
            </Grid>
          </FormProvider>
        </Grid>
        {selectedCandidates.length > 0 ? (
          <Grid container justifyContent="flex-end" spacing={1} xs={5} alignItems="center">
            <Grid item>
              <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.PRIMARY}>
                {`${selectedCandidates.length.toString()} Selected`}
              </WsTypography>
            </Grid>
            {/* <Grid item>
              <WsButton size="medium" variant="outlined" onClick={() => unSelectRow(true)}>
                Unselect All
              </WsButton>
            </Grid>
            <Grid item>
              <WsButton size="medium" variant="outlined" onClick={() => selectAllRow(true)}>
                Select All
              </WsButton>
            </Grid> */}
            <Grid item>
              <WsButton size="medium" onClick={sendEmail} startIcon={<Email />}>
                Send Email
              </WsButton>
            </Grid>
            <Grid item>
              <WsButton size="medium" onClick={sendSms} startIcon={<Sms />}>
                Send SMS
              </WsButton>
            </Grid>
            <Grid item>
              <WsButton
                size="medium"
                onClick={() => {
                  submissionAmplitudeEvent('BULK_UPDATE_SUBMISSIONS_INITIATE', 'Update Button');
                  setOpenStageStatusDrawer(true);
                }}
                startIcon={<LuCopyCheck />}
              >
                Bulk Update
              </WsButton>
            </Grid>
          </Grid>
        ) : (
          <Grid container justifyContent="flex-end" spacing={2} xs={5}>
            <Grid item>
              {permissionState &&
                (workspace?.owns || workspace?.roles?.indexOf('ROLE_DOWNLOAD') !== -1) &&
                isViewDownload && (
                  <ActivateFeature feature="CS_DOWNLOAD_CANDIDATE_LIST">
                    <WsButton
                      onClick={handleOpenDownloadOptions}
                      startIcon={<UploadFileOutlinedIcon />}
                      variant="text"
                    >
                      Export
                    </WsButton>
                  </ActivateFeature>
                )}
              <Menu anchorEl={downloadAnchor} open={open} onClose={() => setDownloadAnchor(null)}>
                <MenuItem onClick={() => onClickDownload('csv')}>Download as CSV</MenuItem>
                <MenuItem onClick={() => onClickDownload('xls')}>Download as XLS</MenuItem>
              </Menu>
            </Grid>
            {!location.pathname.includes('pipeline') && (
              <Grid item>
                <WsButton
                  className="advance-filter"
                  variant="outlined"
                  size="medium"
                  onClick={handleAdvanceFiltersOnClick}
                  startIcon={<BiFilterAlt />}
                >
                  Advanced filters
                </WsButton>
              </Grid>
            )}
            <Grid item>
              <WsButton
                size="medium"
                onClick={() => {
                  if (isSubmitCandidate) {
                    setOpenSubmitCandidate(true);
                  } else {
                    setNotification(warning(accessDeniedNotoficationTxt));
                  }
                }}
                startIcon={<AddIcon />}
              >
                New Submission
              </WsButton>
            </Grid>
          </Grid>
        )}

        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          {openSubmitCandidate && (
            <SubmitCandidate
              candidate={null}
              job={selectedJob}
              close={() => setOpenSubmitCandidate(false)}
              openSubmitCandidate={openSubmitCandidate}
            />
          )}
        </ErrorBoundary>
      </Grid>

      {openStageStatusDrawer && (
        <BulkUpdateStatusDrawer
          candidates={selectedCandidates}
          openStatusModal={openStageStatusDrawer}
          onClose={() => setOpenStageStatusDrawer(false)}
          refreshAll={() => setOpenStageStatusDrawer(false)}
          updateUI={updateUI}
          onUpdateAllClick={() => setOpenStageStatusDrawer(false)}
        />
      )}
      {openEmail && (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <BulkEmail
            open={openEmail}
            handleClose={() => {
              setOpenEmail(false);
              handleSelectedCandidatesLimit(selectedCandidates);
            }}
            candidates={selectedCandidates.filter((candidate) => !candidate.isInDnc)}
            drawer={true}
          />
        </ErrorBoundary>
      )}

      {openBulkSms && (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <BulkSmsDrawer
            open={openBulkSms}
            handleClose={() => {
              setOpenBulkSms(false);
              handleSelectedCandidatesLimit(selectedCandidates);
            }}
            candidates={selectedCandidates.filter((candidate) => !candidate.isInDnc)}
          />
        </ErrorBoundary>
      )}
      <WsDrawer
        title="Download Fields"
        open={openDownload}
        onClose={() => setOpenDownload(false)}
        size="medium"
        headerSize="medium"
      >
        <Box pb={5}>
          <DownloadField
            selectedFields={(d) => downloadCandidates(d)}
            close={() => setOpenDownload(false)}
          />
        </Box>
      </WsDrawer>
    </Box>
  );
};
export default Filters;
